import {
  type CreateProjectRequest,
  deletionResponseSchema,
  groupSchema,
  type PeopleResponse,
  peopleResponseSchema,
  type Person,
  projectDetailSchema,
  projectsResponseSchema,
  type UpdateProjectRequest,
} from '@wednesday/contracts';
import { queryOptions, type QueryClient } from '@tanstack/react-query';

import { apiRequest } from '@/shared/api/client';

/** Server calls of projects and groups (api-design §3.4). */

export const projectKeys = {
  all: ['projects'] as const,
  list: (archived: boolean) => ['projects', 'list', { archived }] as const,
  detail: (projectId: string) => ['projects', 'detail', projectId] as const,
};

export const projectsQueryOptions = (archived = false) =>
  queryOptions({
    queryKey: projectKeys.list(archived),
    queryFn: ({ signal }) =>
      apiRequest('GET', `/projects${archived ? '?archived=true' : ''}`, {
        schema: projectsResponseSchema,
        signal,
      }),
  });

export const projectQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: projectKeys.detail(projectId),
    queryFn: ({ signal }) =>
      apiRequest('GET', `/projects/${projectId}`, { schema: projectDetailSchema, signal }),
  });

/** ADM-3: a deactivated person still owns their history; their name says they left. */
function markDeactivated(response: PeopleResponse): PeopleResponse {
  return {
    items: response.items.map((person) =>
      person.status === 'deactivated'
        ? { ...person, name: `${person.name} (deactivated)` }
        : person,
    ),
  };
}

/**
 * Everyone, without emails — for names wherever a person is shown. Pickers offer only the people
 * `isActive` keeps.
 */
export const peopleQueryOptions = queryOptions({
  queryKey: ['people'],
  queryFn: ({ signal }) => apiRequest('GET', '/users', { schema: peopleResponseSchema, signal }),
  select: markDeactivated,
  staleTime: 60_000,
});

export const isActive = (person: Person) => person.status === 'active';

export function createProject(body: CreateProjectRequest) {
  return apiRequest('POST', '/projects', { body, schema: projectDetailSchema });
}

export function updateProject(projectId: string, body: UpdateProjectRequest) {
  return apiRequest('PATCH', `/projects/${projectId}`, { body, schema: projectDetailSchema });
}

export function replaceMembers(projectId: string, memberIds: string[]) {
  return apiRequest('PUT', `/projects/${projectId}/members`, {
    body: { memberIds },
    schema: projectDetailSchema,
  });
}

export function setArchived(projectId: string, archived: boolean) {
  return apiRequest('POST', `/projects/${projectId}/${archived ? 'archive' : 'unarchive'}`, {
    schema: projectDetailSchema,
  });
}

export function deleteProject(projectId: string) {
  return apiRequest('DELETE', `/projects/${projectId}`, { schema: deletionResponseSchema });
}

export function createGroup(projectId: string, body: { name: string; color?: string }) {
  return apiRequest('POST', `/projects/${projectId}/groups`, { body, schema: groupSchema });
}

export function updateGroup(groupId: string, body: { name?: string; color?: string }) {
  return apiRequest('PATCH', `/groups/${groupId}`, { body, schema: groupSchema });
}

export function moveGroup(groupId: string, afterId: string | null) {
  return apiRequest('POST', `/groups/${groupId}/move`, { body: { afterId }, schema: groupSchema });
}

export function deleteGroup(groupId: string) {
  return apiRequest('DELETE', `/groups/${groupId}`, { schema: deletionResponseSchema });
}

/** TRS-2 / TRS-4: restore a Trash entry (Undo). */
export function restoreDeletion(deletionId: string) {
  return apiRequest('POST', `/trash/${deletionId}/restore`);
}

/** After any project change: refresh lists and the project page. */
export async function invalidateProjects(queryClient: QueryClient) {
  await queryClient.invalidateQueries({ queryKey: projectKeys.all });
}
